import React from 'react';
import {
  Button as CkButton,
  type ButtonProps as CkButtonProps,
  Spinner,
} from '@chakra-ui/react';

type LegacyVariant = 'wide' | 'ghost' | 'filled' | 'outline';

type BtnTypes = {
  title?: string;

  leftIcon?: React.ReactNode;

  size?: 'sm' | 'md' | 'lg';

  variant?: LegacyVariant;

  isLoading?: boolean;

  isDisabled?: boolean;
} & Omit<CkButtonProps, 'loading' | 'disabled' | 'variant' | 'colorPalette'>;

function mapLegacyVariant(variant: LegacyVariant): Pick<
  CkButtonProps,
  'variant' | 'colorPalette'
> & {
  outlineStyle: Partial<CkButtonProps>;
} {
  switch (variant) {
    case 'filled':
    case 'wide':
      return {
        variant: 'solid',

        colorPalette: 'brand',

        outlineStyle: {},
      };

    case 'ghost':
      return {
        variant: 'ghost',

        colorPalette: 'brand',

        outlineStyle: { color: 'primary' },
      };

    case 'outline':
    default:
      return {
        variant: 'outline',

        colorPalette: 'gray',

        outlineStyle: {
          borderColor: 'body2',

          bg: 'body',

          color: 'body2',
        },
      };
  }
}

const Button: React.FC<BtnTypes> = ({
  title,

  leftIcon,

  size = 'md',

  variant = 'filled',

  isLoading,

  isDisabled,

  px,

  _hover,

  ...props
}) => {
  const {
    variant: chakraVariant,
    colorPalette,
    outlineStyle,
  } = mapLegacyVariant(variant);

  const widePx = variant === 'wide' ? '137px' : px;

  const defaultHover =
    variant === 'filled' || variant === 'wide'
      ? { bg: 'darkPrimary' as const }
      : variant === 'ghost'
        ? { bg: 'lightPrimary' as const }
        : variant === 'outline'
          ? { color: 'secondary' as const, bg: 'body' as const }
          : {};

  return (
    <CkButton
      size={size}
      variant={chakraVariant}
      colorPalette={colorPalette}
      h={12}
      disabled={isDisabled || Boolean(isLoading)}
      display="inline-flex"
      gap={leftIcon || isLoading ? 2 : undefined}
      shadow={variant === 'filled' || variant === 'wide' ? 'sm' : undefined}
      px={widePx}
      {...outlineStyle}
      _hover={{ ...defaultHover, ...(_hover as object) }}
      {...props}
    >
      {isLoading ? <Spinner size="sm" /> : null}

      {leftIcon}

      {title}
    </CkButton>
  );
};

export default Button;
